import { Injectable } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { ApiInterface } from '../api-interface';
import { ApiService } from '../api.service';
import {
  AlterarFlagContadorDTO,
  AprovarSolicitacaoContadorDTO,
  ClienteContadorResponseDTO,
  ClienteDisponivelContratoDTO,
  ContaExistenteContratoDTO,
  ContratoContadorConsultaDTO,
  ContratoContadorRelatorioFiltroDTO,
  CupomContadorConsultaDTO,
  DefinirAutonomiaContadorDTO,
  DefinirMesReferenciaContratoDTO,
  MesReferencia,
  ContratoContadorResponseDTO,
  MeuDescontoVigenteDTO,
  MinhaSituacaoContadorDTO,
  PercentualComissaoAprovarDTO,
  PercentualContratoContadorConsultaDTO,
  RankingContadorConsultaDTO,
  RecusarSolicitacaoContadorDTO,
  SolicitacaoContratoRecebidaDTO,
  SolicitarContratoContadorDTO,
  SolicitarContratoResultadoDTO,
  ResumoContadorConsultaDTO,
  SolicitacaoContadorConsultaDTO,
  SolicitacaoContadorDTO,
  SolicitacaoContadorResponseDTO,
  StatusSolicitacaoContador,
} from '../../models/contador/contador.model';
import {
  AdicionarParcelasDTO,
  CancelamentoParcelasLoteResultadoDTO,
  GerarParcelasLoteDTO,
  GerarParcelasLoteResultadoDTO,
  ParcelaContratoClienteFiltroDTO,
  ParcelaContratoFiltroDTO,
  PageParcelaContratoDTO,
  PrepararFaturamentoDTO,
  VincularFaturamentoResultadoDTO,
} from '../../models/contador/parcela-contrato.model';

/**
 * Service de Contador Parceiro.
 *
 * Cobre os endpoints REST do backend (ver `API_CONTRACT_contador.md`):
 * - GET   /contador/minha-situacao             (situacao do usuario autenticado)
 * - POST  /contador/solicitacoes               (solicita virar contador)
 * - GET   /contador/solicitacoes               (listagem paginada — master)
 * - PATCH /contador/solicitacoes/{id}/aprovar  (aprova — master)
 * - PATCH /contador/solicitacoes/{id}/recusar  (recusa — master)
 * - PATCH /usuario/{id}/contador               (altera flag direto — master, SEM UI)
 *
 * Implementa `ApiInterface` (padrao `IndicacaoService`). `put`/`delete` nao se
 * aplicam a esta feature; sao no-op coerentes apenas para satisfazer a interface.
 */
@Injectable({ providedIn: 'root' })
export class ContadorService implements ApiInterface {
  constructor(private apiService: ApiService) {}

  // ---------------------------------------------------------------------------
  // ApiInterface
  // ---------------------------------------------------------------------------

  /** Listagem paginada de solicitacoes (master). url ja com query string. */
  get<T>(url: string, data?: any): Observable<SolicitacaoContadorResponseDTO> {
    const queryParams = this.apiService.buildQueryParams(data);
    const path = queryParams ? `${url}?${queryParams}` : url;
    return this.apiService.read<SolicitacaoContadorResponseDTO>(path);
  }

  /** Criar solicitacao (POST /contador/solicitacoes). */
  post<T>(url: string, data: SolicitacaoContadorDTO): Observable<SolicitacaoContadorConsultaDTO> {
    return this.apiService.create<SolicitacaoContadorConsultaDTO>(url, data);
  }

  /** Nao se aplica (sem PUT). No-op coerente. */
  put<T>(_url: string, _data: any): Observable<any> {
    return EMPTY;
  }

  /** Nao se aplica (sem DELETE). No-op coerente. */
  delete<T>(_url: string): Observable<any> {
    return EMPTY;
  }

  // ---------------------------------------------------------------------------
  // Metodos especificos da feature
  // ---------------------------------------------------------------------------

  /** GET /contador/minha-situacao — situacao do usuario autenticado. */
  minhaSituacao(): Observable<MinhaSituacaoContadorDTO> {
    return this.apiService.read<MinhaSituacaoContadorDTO>('/contador/minha-situacao');
  }

  /** POST /contador/solicitacoes — solicita virar contador (observacao opcional). */
  solicitar(observacao?: string): Observable<SolicitacaoContadorConsultaDTO> {
    const body: SolicitacaoContadorDTO = observacao ? { observacao } : {};
    return this.post('/contador/solicitacoes', body);
  }

  /**
   * GET /contador/solicitacoes — listagem paginada master.
   * `status` nao existe em ApiQueryParamsDTO; concatena-se manualmente a query.
   * `termoBusca` (opcional) filtra por nome OU e-mail do solicitante (contains,
   * case-insensitive, sem acentos) e combina com o `status` (AND). Quando vazio,
   * o parametro nao e enviado.
   */
  listarSolicitacoes(
    status: StatusSolicitacaoContador,
    page: number,
    size: number,
    termoBusca?: string,
    sort: string = 'solicitadoEm,desc',
  ): Observable<SolicitacaoContadorResponseDTO> {
    let query = `status=${status}&page=${page}&size=${size}&sort=${sort}`;
    const termo = (termoBusca || '').trim();
    if (termo) {
      query += `&termoBusca=${encodeURIComponent(termo)}`;
    }
    return this.apiService.read<SolicitacaoContadorResponseDTO>(`/contador/solicitacoes?${query}`);
  }

  /**
   * PATCH /contador/solicitacoes/{id}/aprovar — aprova a solicitacao. Alem de
   * marcar a empresa como contadora, cria a inscricao dela no plano informado
   * (`planoId`, OBRIGATORIO). Aceita QUALQUER plano ATIVO: ESPECIAL => inscricao
   * perpetua; comum => vence pela periodicidade — sempre sem cobranca. Tambem
   * libera os percentuais de desconto (cada um com a comissao %), que sao opcionais.
   * `motivo` (opcional, max 500) grava `motivoDecisao` — campo unico da decisao
   * (aprovacao e recusa), separado da `observacao` do solicitante.
   * `usuariosPermitidos` (opcional) e o TOTAL de acessos do contador, NAO um incremento;
   * omitido fica o do plano. Entre 1 e 100 — senao 422 `contador_usuarios_invalido`.
   * Body: { planoId, motivo?, usuariosPermitidos?, percentuais: [{ percentual, comissao }] }.
   */
  aprovar(
    id: number,
    planoId: number,
    percentuais: PercentualComissaoAprovarDTO[] = [],
    motivo?: string,
    usuariosPermitidos?: number | null,
  ): Observable<SolicitacaoContadorConsultaDTO> {
    const body: AprovarSolicitacaoContadorDTO = {
      planoId,
      percentuais,
    };
    // Motivo e OPCIONAL: enviado (trim) somente quando preenchido — em branco o
    // backend nao grava `motivoDecisao`.
    const motivoTrim = motivo?.trim();
    if (motivoTrim) {
      body.motivo = motivoTrim;
    }
    // OPCIONAL: so vai no corpo quando informado — omitir mantem o que o plano ja inclui.
    if (usuariosPermitidos !== undefined && usuariosPermitidos !== null) {
      body.usuariosPermitidos = usuariosPermitidos;
    }
    return this.apiService.patch<SolicitacaoContadorConsultaDTO>(
      `/contador/solicitacoes/${id}/aprovar`,
      body,
    );
  }

  /**
   * PATCH /contador/solicitacoes/{id}/recusar — `motivo` (opcional) grava
   * `motivoDecisao`, o mesmo campo da aprovacao (a observacao do solicitante fica intacta).
   */
  recusar(id: number, motivo?: string): Observable<SolicitacaoContadorConsultaDTO> {
    const body: RecusarSolicitacaoContadorDTO = motivo ? { motivo } : {};
    return this.apiService.patch<SolicitacaoContadorConsultaDTO>(
      `/contador/solicitacoes/${id}/recusar`,
      body,
    );
  }

  /**
   * PATCH /usuario/{id}/contador — altera a flag direto (master-only).
   * PRONTO no service, mas SEM UI nesta entrega (futura Gestao de Usuarios).
   */
  alterarFlag(usuarioId: number, contador: boolean): Observable<any> {
    const body: AlterarFlagContadorDTO = { contador };
    return this.apiService.patch(`/usuario/${usuarioId}/contador`, body);
  }

  // ---------------------------------------------------------------------------
  // Dashboard da Area do Contador (read-only) — API_CONTRACT_contador-dashboard
  // ---------------------------------------------------------------------------

  /** GET /contador/clientes — lista paginada (lazy) dos clientes do contador. */
  listarClientes(
    page: number,
    size: number,
    sort: string = 'criadoEm,desc',
  ): Observable<ClienteContadorResponseDTO> {
    const query = `page=${page}&size=${size}&sort=${sort}`;
    return this.apiService.read<ClienteContadorResponseDTO>(`/contador/clientes?${query}`);
  }

  /**
   * GET /contador/contratos — lista paginada (lazy) dos contratos do contador
   * (todos os status). Aceita filtros OPCIONAIS (`status`/`de`/`ate`) via
   * `filtro`; retrocompativel (sem filtro = comportamento anterior). Os mesmos
   * filtros valem para a tabela e para o relatorio PDF.
   *
   * `buildQueryParams` descarta chaves `undefined`, entao so os filtros
   * preenchidos vao na query (mesmo padrao de `listarDoContador` das despesas).
   */
  listarContratos(
    page: number,
    size: number,
    sort: string = 'dataAssociacao,desc',
    filtro?: ContratoContadorRelatorioFiltroDTO,
  ): Observable<ContratoContadorResponseDTO> {
    const query = this.apiService.buildQueryParams({ page, size, sort, ...(filtro || {}) });
    const path = query ? `/contador/contratos?${query}` : '/contador/contratos';
    return this.apiService.read<ContratoContadorResponseDTO>(path);
  }

  /**
   * GET /contador/contratos/relatorio-pdf — PDF (Base64) dos contratos do
   * contador conforme os MESMOS filtros da aba (`status`/`de`/`ate`), SEM
   * page/size (imprime tudo ate o teto do backend; sort default do backend).
   * Contador-only (403 quando a empresa nao e contadora); 422 quando excede o
   * teto de 5000 contratos.
   *
   * A resposta e `text/plain` com o Base64 PURO do PDF, por isso usamos
   * `apiService.readText` (NUNCA `read`, que faria JSON.parse e quebraria o
   * Base64). O cast `filtro as any` e necessario porque `ApiQueryParamsDTO` so
   * declara page/size/sort/filter (mesmo padrao de `gerarRelatorioDespesasPdf`).
   * Ver `vendas/docs/api/API_CONTRACT_relatorio-contratos-contador.md`.
   */
  gerarRelatorioContratosPdf(
    filtro: ContratoContadorRelatorioFiltroDTO,
  ): Observable<string> {
    const queryParams = this.apiService.buildQueryParams(filtro as any);
    const path = queryParams
      ? `/contador/contratos/relatorio-pdf?${queryParams}`
      : '/contador/contratos/relatorio-pdf';
    return this.apiService.readText<string>(path);
  }

  /**
   * GET /contador/contratos/relatorio-export?...&formato=csv|xlsx — baixa os
   * contratos como arquivo binário, com os MESMOS filtros do PDF (sem
   * page/size/sort). Blob vazio = sem dados; o corpo do erro chega como Blob,
   * então o componente roteia por `err.status`. Cast `as any`: `ApiQueryParamsDTO`
   * não declara `formato` (mesmo padrão de `gerarRelatorioDespesasArquivo`).
   * Contrato: `vendas/docs/api/API_CONTRACT_export-contratos-contador-csv-xlsx.md`.
   */
  gerarRelatorioContratosArquivo(
    formato: 'csv' | 'xlsx',
    filtro: ContratoContadorRelatorioFiltroDTO,
  ): Observable<Blob> {
    const queryParams = this.apiService.buildQueryParams({ ...filtro, formato } as any);
    return this.apiService.readBlob(`/contador/contratos/relatorio-export?${queryParams}`);
  }

  /**
   * GET /contador/contratos/proximos-vencimento — contratos ATIVOS perto do
   * vencimento, filtrados por intervalo de `dataVencimento` (`de`/`ate`, ambos
   * opcionais, ISO `yyyy-MM-dd`). Ordenado por `dataVencimento` ASC no backend.
   */
  listarProximosVencimento(
    de: string | undefined,
    ate: string | undefined,
    page: number,
    size: number,
    sort: string = 'dataVencimento,asc',
  ): Observable<ContratoContadorResponseDTO> {
    // Variavel intermediaria: evita a checagem de excesso de propriedades do
    // object literal (buildQueryParams itera as chaves em runtime).
    const params = { de, ate, page, size, sort };
    const query = this.apiService.buildQueryParams(params);
    const path = query
      ? `/contador/contratos/proximos-vencimento?${query}`
      : '/contador/contratos/proximos-vencimento';
    return this.apiService.read<ContratoContadorResponseDTO>(path);
  }

  /**
   * POST /contador/contratos/parcelas/gerar-lote — gera parcelas em lote para os
   * contratos selecionados (`contratoIds`), com `quantidade` e `periodicidade`.
   * Retorna o total processado/gerado e os contratos ignorados (com motivo).
   */
  gerarParcelasLote(
    body: GerarParcelasLoteDTO,
  ): Observable<GerarParcelasLoteResultadoDTO> {
    return this.apiService.create<GerarParcelasLoteResultadoDTO>(
      '/contador/contratos/parcelas/gerar-lote',
      body,
    );
  }

  /** GET /contador/contratos/clientes-disponiveis — clientes indicados elegiveis para um novo contrato. */
  listarClientesDisponiveis(): Observable<ClienteDisponivelContratoDTO[]> {
    return this.apiService.read<ClienteDisponivelContratoDTO[]>('/contador/contratos/clientes-disponiveis');
  }

  /**
   * GET /contador/contratos/buscar-empresas — checa, por email EXATO, se o cliente ja tem conta
   * antes de o contador criar uma nova. A resposta traz apenas o eco do email consultado
   * (sem nome e sem id), por seguranca.
   */
  buscarEmpresasParaContrato(email: string): Observable<ContaExistenteContratoDTO[]> {
    return this.apiService.read<ContaExistenteContratoDTO[]>(
      `/contador/contratos/buscar-empresas?email=${encodeURIComponent(email)}`,
    );
  }

  /** GET /contador/contratos/percentuais — percentuais de desconto liberados ao contador. */
  listarPercentuaisLiberados(): Observable<PercentualContratoContadorConsultaDTO[]> {
    return this.apiService.read<PercentualContratoContadorConsultaDTO[]>('/contador/contratos/percentuais');
  }

  /** GET /contador/contratos/cupons — cupons ativos do contador (desconto + comissao). */
  listarCuponsLiberados(): Observable<CupomContadorConsultaDTO[]> {
    return this.apiService.read<CupomContadorConsultaDTO[]>('/contador/contratos/cupons');
  }

  /**
   * POST /contador/contratos — solicita um contrato por cliente (id) ou por email.
   * Retorna CONTRATO (criado) ou CONVITE (email sem conta).
   */
  listarSolicitacoesRecebidas(): Observable<SolicitacaoContratoRecebidaDTO[]> {
    return this.apiService.read<SolicitacaoContratoRecebidaDTO[]>('/contador/contratos/solicitacoes-recebidas');
  }

  aceitarContrato(id: number): Observable<ContratoContadorConsultaDTO> {
    return this.apiService.patch<ContratoContadorConsultaDTO>(`/contador/contratos/${id}/aceitar`, {});
  }

  recusarContrato(id: number): Observable<ContratoContadorConsultaDTO> {
    return this.apiService.patch<ContratoContadorConsultaDTO>(`/contador/contratos/${id}/recusar`, {});
  }

  /**
   * PATCH /contador/contratos/{id}/autonomia — o CLIENTE dono do contrato ATIVO
   * concede (true) ou revoga (false) a autonomia do contador. Com autonomia
   * LIGADA, os gastos lancados pelo contador nascem APROVADOS (sem passar pela
   * aprovacao do cliente). Retorna o contrato atualizado (com `autonomiaContador`).
   */
  definirAutonomia(contratoId: number, autonomia: boolean): Observable<ContratoContadorConsultaDTO> {
    const body: DefinirAutonomiaContadorDTO = { autonomia };
    return this.apiService.patch<ContratoContadorConsultaDTO>(
      `/contador/contratos/${contratoId}/autonomia`,
      body,
    );
  }

  solicitarContrato(dto: SolicitarContratoContadorDTO): Observable<SolicitarContratoResultadoDTO> {
    return this.apiService.create<SolicitarContratoResultadoDTO>('/contador/contratos', dto);
  }

  /**
   * PATCH /contador/contratos/{id}/cancelar — o CONTADOR cancela um contrato
   * ATIVO/PENDENTE (-> CANCELADO). `dataCancelamento` (ISO `yyyy-MM-dd`, opcional)
   * é a data efetiva: vira a data de encerramento e o corte para cancelar os
   * títulos/parcelas com vencimento a partir dela. Ausente => hoje. Resposta 204.
   */
  cancelarContrato(id: number, dataCancelamento?: string): Observable<void> {
    const body = dataCancelamento ? { dataCancelamento } : {};
    return this.apiService.patch<void>(`/contador/contratos/${id}/cancelar`, body);
  }

  /**
   * PATCH /contador/contratos/{contratoId}/mes-referencia — altera o mês de
   * referência (competência) das parcelas do contrato. Recalcula a competência
   * das parcelas PENDENTES. Retorna o contrato atualizado.
   */
  definirMesReferencia(
    contratoId: number,
    mesReferencia: MesReferencia,
  ): Observable<ContratoContadorConsultaDTO> {
    const body: DefinirMesReferenciaContratoDTO = { mesReferencia };
    return this.apiService.patch<ContratoContadorConsultaDTO>(
      `/contador/contratos/${contratoId}/mes-referencia`,
      body,
    );
  }

  /**
   * PATCH /contador/contratos/{id}/valor-mensal — altera o valor mensal do
   * contrato; o backend propaga para as parcelas/títulos em aberto sem pagamento
   * parcial.
   */
  alterarValorMensalContrato(
    contratoId: number,
    valorMensal: number,
  ): Observable<ContratoContadorConsultaDTO> {
    return this.apiService.patch<ContratoContadorConsultaDTO>(
      `/contador/contratos/${contratoId}/valor-mensal`,
      { valorMensal },
    );
  }

  /**
   * PATCH /contador/contratos/{id}/dia-cobranca — altera o dia de cobrança do
   * contrato; o backend propaga para o vencimento das parcelas/títulos em aberto
   * sem pagamento parcial.
   */
  alterarDiaCobrancaContrato(
    contratoId: number,
    diaCobranca: number,
  ): Observable<ContratoContadorConsultaDTO> {
    return this.apiService.patch<ContratoContadorConsultaDTO>(
      `/contador/contratos/${contratoId}/dia-cobranca`,
      { diaCobranca },
    );
  }

  /**
   * PATCH /contador/contratos/{id}/parcelas-por-mes — altera as parcelas por mês
   * do contrato. Vale só para as próximas parcelas geradas (não recalcula as
   * existentes).
   */
  alterarParcelasPorMesContrato(
    contratoId: number,
    parcelasPorMes: number,
  ): Observable<ContratoContadorConsultaDTO> {
    return this.apiService.patch<ContratoContadorConsultaDTO>(
      `/contador/contratos/${contratoId}/parcelas-por-mes`,
      { parcelasPorMes },
    );
  }

  /**
   * GET /contador/contratos/{contratoId}/parcelas — lista PAGINADA as parcelas
   * mensais do contrato (todos os estados). Somente leitura.
   *
   * Parâmetros de paginação/ordenação (formato Spring):
   * - `page`: índice 0-based; `size`: itens por página (default backend: 20);
   * - `sort`: `campo,direcao` (default backend: `numeroParcela,asc`); campos
   *   aceitos: `numeroParcela`, `dataReferencia`, `dataVencimento`,
   *   `valorParcela`, `saldo`, `estadoCliente`, `estadoContador`.
   *
   * `buildQueryParams` descarta chaves `undefined`, então omitir `sort` deixa o
   * backend aplicar o default.
   */
  listarParcelas(
    contratoId: number,
    page: number = 0,
    size: number = 10,
    sort?: string,
    filtros?: ParcelaContratoFiltroDTO,
  ): Observable<PageParcelaContratoDTO> {
    // Params com filtros de dominio (de/ate/situacao) alem de page/size/sort.
    // Atribui a uma variavel para nao cair na checagem de excesso de propriedades
    // do object literal (buildQueryParams itera as chaves em runtime).
    const params = {
      page,
      size,
      sort,
      de: filtros?.de,
      ate: filtros?.ate,
      situacao: filtros?.situacao,
    };
    const query = this.apiService.buildQueryParams(params);
    const url = `/contador/contratos/${contratoId}/parcelas`;
    const path = query ? `${url}?${query}` : url;
    return this.apiService.read<PageParcelaContratoDTO>(path);
  }

  /**
   * POST /contador/contratos/{contratoId}/parcelas — adiciona parcelas ao
   * contrato ativo. `quantidade` (> 0) e `periodicidade` (> 0, nº de parcelas
   * por mês). Retorna o contrato atualizado (com a nova `dataVencimento`).
   */
  adicionarParcelas(
    contratoId: number,
    body: AdicionarParcelasDTO,
  ): Observable<ContratoContadorConsultaDTO> {
    return this.apiService.create<ContratoContadorConsultaDTO>(
      `/contador/contratos/${contratoId}/parcelas`,
      body,
    );
  }

  /**
   * PATCH /contador/contratos/parcelas/cancelar-lote — cancela em lote as
   * parcelas dos ids informados (cancela também os títulos vinculados). Só
   * cancela parcelas ainda ativas; já canceladas/inexistentes viram `ignoradas`
   * com motivo amigável, sem falhar o lote.
   */
  cancelarParcelasEmLote(
    ids: number[],
  ): Observable<CancelamentoParcelasLoteResultadoDTO> {
    return this.apiService.patch<CancelamentoParcelasLoteResultadoDTO>(
      '/contador/contratos/parcelas/cancelar-lote',
      { ids },
    );
  }

  /**
   * POST /contador/contratos/parcelas/preparar-faturamento — passo 1 do faturar.
   * Recebe os ids das parcelas e devolve os dados para o FRONT montar a venda
   * (cliente, item de serviço, valor total, período, vencimento). Erros: 404
   * (parcela de outro contador), 422 (não PENDENTE) e 422 (multi-contrato).
   */
  prepararFaturamento(
    ids: number[],
    percentualJuros?: number | null,
  ): Observable<PrepararFaturamentoDTO> {
    return this.apiService.create<PrepararFaturamentoDTO>(
      '/contador/contratos/parcelas/preparar-faturamento',
      { ids, percentualJuros: percentualJuros ?? null },
    );
  }

  /**
   * PATCH /contador/contratos/parcelas/vincular-faturamento — passo 2 do faturar,
   * chamado APÓS o front criar a nota. Vincula as parcelas à nota criada
   * (`notaId`) e retorna o total vinculado + as ignoradas. `percentualJuros`
   * (mesmo do preparar) soma o juros das parcelas atrasadas no título a pagar
   * do cliente.
   */
  vincularFaturamento(
    ids: number[],
    notaId: number,
    percentualJuros?: number | null,
  ): Observable<VincularFaturamentoResultadoDTO> {
    return this.apiService.patch<VincularFaturamentoResultadoDTO>(
      '/contador/contratos/parcelas/vincular-faturamento',
      { ids, notaId, percentualJuros: percentualJuros ?? null },
    );
  }

  /**
   * GET /contador/contratos/meu-desconto-vigente — desconto do contador vigente da
   * empresa autenticada (lado cliente). Corpo vazio (200) => null (sem contrato ATIVO, D1).
   */
  getMeuDescontoVigente(): Observable<MeuDescontoVigenteDTO | null> {
    return this.apiService.read<MeuDescontoVigenteDTO | null>('/contador/contratos/meu-desconto-vigente');
  }

  /**
   * GET /cliente/contratos/{contratoId}/parcelas — lista PAGINADA as parcelas do
   * contrato do CLIENTE autenticado (mensalidade do serviço do contador).
   * Somente leitura. O backend valida a posse do contrato (404 se não pertencer
   * à empresa do cliente — sem IDOR).
   *
   * Espelha `listarParcelas` (contador), trocando o path e o tipo de filtro
   * (`situacao = EstadoClienteParcela` via `ParcelaContratoClienteFiltroDTO`).
   * `buildQueryParams` descarta chaves `undefined` (sort/de/ate/situacao são
   * opcionais), deixando o backend aplicar seus defaults.
   */
  listarParcelasDoMeuContrato(
    contratoId: number,
    page: number = 0,
    size: number = 10,
    sort?: string,
    filtros?: ParcelaContratoClienteFiltroDTO,
  ): Observable<PageParcelaContratoDTO> {
    // Atribui a uma variavel para nao cair na checagem de excesso de propriedades
    // do object literal (buildQueryParams itera as chaves em runtime).
    const params = {
      page,
      size,
      sort,
      de: filtros?.de,
      ate: filtros?.ate,
      situacao: filtros?.situacao,
    };
    const query = this.apiService.buildQueryParams(params);
    const url = `/cliente/contratos/${contratoId}/parcelas`;
    const path = query ? `${url}?${query}` : url;
    return this.apiService.read<PageParcelaContratoDTO>(path);
  }

  /** GET /contador/resumo — resumo agregado do contador. */
  resumo(): Observable<ResumoContadorConsultaDTO> {
    return this.apiService.read<ResumoContadorConsultaDTO>('/contador/resumo');
  }

  /** GET /contador/ranking — posicao do contador entre contadores (sem leaderboard). */
  ranking(): Observable<RankingContadorConsultaDTO> {
    return this.apiService.read<RankingContadorConsultaDTO>('/contador/ranking');
  }
}
